import moment from "moment";
import {
  CombinedIncome,
  VerificationSpecificAdditionaData
} from "../../../types/income";
import { HousingAssistanceSlice } from "../HousingAssistanceSlice";
import { Section } from "views/applications-id-report/components/Section";
import {
  getIncomeJobName,
  getIncomeOriginalNetAndGrossDollars,
  incomeTypeMap
} from "../../../utils";
import { has } from "shared/utils";
import { Show } from "shared/components";
import { SummaryUpdated } from "../../../components/SummaryUpdated";
import { useMemo, useState } from "react";
import { IncomeStatus, ReverifyRequest } from "shared/types";
import { useCombinedIncomeContext } from "../../../providers/useCombinedIncome";
import { formatMonths } from "../../../helpers";
import { isMultipleSourcesBannerVisible } from "../../../utils";
import { IncomeSourceStatusBadge } from "../../../components/IncomeSourceStatusBadge";
import { IncomeStats } from "./components/IncomeStats";
import { SelfReportedStats } from "./components/SelfReportedStats";
import { IncomeHeader } from "./components/IncomeHeader";
import { ProcessingStatus } from "./components/ProcessingStatus";
import { ReverifyAttempt } from "../../ReverifySlice/types";
import {
  isReverifyTabAPlaceholder,
  getReverifyAttemptsPerIncome
} from "../../ReverifySlice/utils";
import { VerifiedIncomeDetails } from "./components/VerifiedIncomeDetails";
import { Banner } from "shared/components/Banner";
import { HugeiconsIcon } from "@hugeicons/react";
import { Money03Icon } from "@hugeicons-pro/core-stroke-rounded";

export const IncomeSlice = ({
  income,
  additionalData,
  pendingReverifyRequest,
  completedReverifyRequest,
  failedReverifyRequest
}: {
  income: CombinedIncome;
  additionalData?: VerificationSpecificAdditionaData | null;
  pendingReverifyRequest?: ReverifyRequest | null;
  completedReverifyRequest?: ReverifyRequest | null;
  failedReverifyRequest?: ReverifyRequest | null;
}) => {
  const { applicant, plaidBankIncomeSources, verificationData } =
    useCombinedIncomeContext();

  const showMultipleSourcesBanner = isMultipleSourcesBannerVisible(
    income,
    plaidBankIncomeSources,
    verificationData?.created_at
  );

  const [selectedReverifyAttemptIdx, setSelectedReverifyAttemptIdx] =
    useState(0);
  const diff = moment().diff(moment(income.start_date), "months");

  const reverifyAttempts: ReverifyAttempt[] = useMemo(
    () =>
      getReverifyAttemptsPerIncome({
        income,
        additionalData,
        pendingReverifyRequest,
        completedReverifyRequest,
        failedReverifyRequest,
        isManualApplicant: applicant.is_manual
      }),
    [
      income,
      additionalData,
      pendingReverifyRequest,
      completedReverifyRequest,
      failedReverifyRequest,
      applicant.is_manual
    ]
  );
  const selectedReverifyAttempt = reverifyAttempts[selectedReverifyAttemptIdx];

  const { originalNetMonthlyIncomeDollars, originalGrossMonthlyIncomeDollars } =
    getIncomeOriginalNetAndGrossDollars(income, selectedReverifyAttempt);

  const isIncomeIncomplete = [
    IncomeStatus.Processing,
    IncomeStatus.Pending
  ].includes(income.status);

  const hasInProgressReverify = Boolean(
    pendingReverifyRequest ||
    (completedReverifyRequest && income.status === IncomeStatus.Processing)
  );

  if (income.type === "housing_assistance")
    return (
      <HousingAssistanceSlice
        amountCents={income.amount_cents}
        assets={income.assets}
        incomeId={income.id}
        applicant={applicant}
        verificationId={income.verification_id}
      />
    );

  return (
    <Section
      id={`combined-income-section-${income.id}`}
      maxWidth="1000px"
      sectionTitle={
        <p className="flex items-center gap-2 text-content-400 font-medium text-sm">
          {income.additional_data ? (
            <>
              {getIncomeJobName(income.additional_data)}
              <span className="block size-1.5 shrink-0 bg-content-100 rounded-full" />
            </>
          ) : (
            ""
          )}
          {incomeTypeMap[income.type] || income.type}
          <span className="block size-1.5 shrink-0 bg-content-100 rounded-full" />
          <IncomeSourceStatusBadge
            income={income}
            forceInProgress={isReverifyTabAPlaceholder(selectedReverifyAttempt)}
            bankStatements={
              additionalData?.bank_statement?.allBankStatements || {}
            }
          />
        </p>
      }
      sectionClassName="md:p-6 md:pt-8 pt-3 pl-4 pb-12"
      rightElement={
        <p className="text-content-400 text-sm font-medium">
          {income.type === "offer_letter"
            ? moment(income.start_date).format("ll")
            : `${moment(income.start_date).format("ll")} - Present (${formatMonths(diff)})`}
        </p>
      }
    >
      <Show if={showMultipleSourcesBanner}>
        <div className="mb-6">
          <Banner
            variant="primary"
            textTheme="colorful"
            title="New: Multiple income sources"
            body={`Now you can view all income sources found in the connected bank accounts. Choose which income source to add to calculations using the "Change" button.`}
            HugeIcon={
              <HugeiconsIcon
                icon={Money03Icon}
                className="shrink-0 text-primary-500"
                size="20"
              />
            }
          />
        </div>
      </Show>
      <IncomeHeader
        income={income}
        selectedReverifyAttempt={selectedReverifyAttempt}
        allBankStatements={
          additionalData?.bank_statement?.allBankStatements || {}
        }
        isIncomeIncomplete={isIncomeIncomplete}
        hasInProgressReverify={hasInProgressReverify}
        hasFailedReverify={Boolean(failedReverifyRequest)}
      />
      <IncomeStats
        income={income}
        selectedReverifyAttempt={selectedReverifyAttempt}
        forcePlaceholdersDisplay={isReverifyTabAPlaceholder(
          selectedReverifyAttempt
        )}
        bankStatements={additionalData?.bank_statement?.allBankStatements || {}}
      />
      <Show
        if={
          !isReverifyTabAPlaceholder(selectedReverifyAttempt) &&
          (has(income.additional_data?.overrides?.gross_monthly_income) ||
            has(income.additional_data?.overrides?.net_monthly_income))
        }
      >
        <SummaryUpdated
          className="mb-10"
          changes={income.additional_data?.overrides}
          original={{
            gross: originalGrossMonthlyIncomeDollars,
            net: originalNetMonthlyIncomeDollars
          }}
        />
      </Show>

      <SelfReportedStats income={income} />
      {income.status === "processing" &&
      !(pendingReverifyRequest || completedReverifyRequest) ? (
        <ProcessingStatus income={income} />
      ) : (
        <VerifiedIncomeDetails
          reverifyAttempts={reverifyAttempts}
          income={income}
          selectedReverifyAttemptIdx={selectedReverifyAttemptIdx}
          setSelectedReverifyAttemptIdx={setSelectedReverifyAttemptIdx}
          selectedReverifyAttempt={selectedReverifyAttempt}
          additionalData={additionalData}
          pendingReverifyRequest={pendingReverifyRequest}
        />
      )}
    </Section>
  );
};
